/* eslint-disable no-nested-ternary */
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import displayInYen from '../../../core/displayInYen';
import type { Hotel } from '../../../core/isValidHotel';
import Button from '../../common/Button';
import ImageWithFallback from '../../common/ImageWithFallback';
import { hotelGeneralInfoTestIds as testIds } from './testIds';
import dispatchGtmAppEvent from '../../../core/googleTagManagerEvents/googleTagManagerEvents';
import { StartPrices } from '../../../../types/StartPrices';
import Loading from '../../../../public/animations/loading.json';
import { useSelector } from '../../../core/redux/store';
import ReviewAndAccess from '../ReviewAndAccess/ReviewAndAccess';

const Lottie = dynamic(() => import('../../common/Lottie/Lottie'));

type Props = {
  hotel: Hotel | undefined;
  scrollToAccess?: () => void;
  campaignsForTags: Hotel['webCampaigns'];
  isBookingEngineEnabled: boolean;
  startPrices?: StartPrices;
  isStartPricesLoading: boolean;
};

const HotelGeneralInfo = ({
  hotel,
  scrollToAccess,
  campaignsForTags,
  isBookingEngineEnabled,
  startPrices,
  isStartPricesLoading,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const startPrice = startPrices?.startPrice;
  const discountedStartPrice =
    isLoggedIn && startPrices?.membersDiscountedStartPrice
      ? startPrices?.membersDiscountedStartPrice
      : startPrices?.discountedStartPrice;

  const handleButtonClick = () => {
    const { id } = hotel || {};
    if (id) {
      const value = startPrice ? Number(startPrice) : null;
      dispatchGtmAppEvent({
        appEventId: 'booking_button_clicked',
        gaEventName: 'booking_start',
        gaCategory: 'booking_start',
        gaAction: 'click',
        gaLabel: id,
        gaValue: value,
        hotelId: id,
        transactionValue: value,
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div>
        <div className="flex flex-wrap gap-1">
          {campaignsForTags?.map(
            (x) =>
              x.image?.url && (
                <div className="mb-1" key={`${x.discount}_${x.image.url}`}>
                  <ImageWithFallback
                    src={x.image.url}
                    alt={x.image.alt || ''}
                    fallbackSrc="/fallback-img-3704x2304.png"
                    layout="fixed"
                    objectFit="cover"
                    width={x.image.width}
                    height={x.image.height}
                  />
                </div>
              )
          )}
        </div>
        <h1 data-testid={testIds.hotel_name} className="text-title3 font-bold sm:text-title2">
          {hotel?.name}
        </h1>
        {hotel && <ReviewAndAccess hotel={hotel} scrollToAccess={scrollToAccess} />}
      </div>

      {!isBookingEngineEnabled && (
        <div className="flex w-full flex-col flex-wrap items-end justify-between pt-6 sm:flex-row sm:pt-5">
          {isStartPricesLoading ? (
            <div className="h-14">
              <Lottie
                style={{ width: 'auto', height: '100%' }}
                params={{
                  loop: true,
                  animationData: Loading,
                }}
              />
            </div>
          ) : startPrice ? (
            discountedStartPrice ? (
              <div className="sm:justify-none flex w-full items-end justify-end pt-0 sm:w-auto">
                <p className="pr-2 text-small text-gray-600">
                  {t('hotel:hotel_general_info.price_for_adult_one_night')}
                </p>
                <div className="flex flex-col items-end">
                  {isStartPricesLoading && (
                    <Lottie
                      style={{ width: '100%', height: '100%' }}
                      params={{
                        loop: true,
                        animationData: Loading,
                      }}
                    />
                  )}
                  <p
                    data-testid={testIds.hotel_cheapest_price}
                    className="text-regular text-orange-700 line-through"
                  >
                    {displayInYen(Number(startPrice), router.locale)} ~
                  </p>
                  <p
                    data-testid={testIds.hotel_discounted_price}
                    className="text-title2 font-bold"
                    style={{ lineHeight: '1em' }}
                  >
                    {displayInYen(discountedStartPrice, router.locale)} ~
                  </p>
                </div>
              </div>
            ) : (
              <div className="sm:justify-none flex w-full items-baseline justify-end pt-0 sm:w-auto">
                <p className="pr-2 text-small text-gray-600">
                  {t('hotel:hotel_general_info.price_for_adult_one_night')}
                </p>
                <p data-testid={testIds.hotel_cheapest_price} className="text-title2 font-bold">
                  {displayInYen(Number(startPrice), router.locale)} ~
                </p>
              </div>
            )
          ) : (
            <div className="flex" /> // spacer
          )}

          <div className="hidden pt-2 md:block">
            <Button
              className="hotel-booking-button"
              data-testid={testIds.reservation_button_desktop}
              to={hotel?.bookingEngineExternalLink}
              color="red"
              onClick={handleButtonClick}
            >
              {t('common:button.plan_details_and_book')}
            </Button>
          </div>
          <div className="block w-full pt-6 md:hidden">
            <Button
              className="hotel-booking-button-mobile w-full"
              data-testid={testIds.reservation_button_mobile}
              to={hotel?.bookingEngineExternalLink}
              color="red"
              onClick={handleButtonClick}
            >
              {t('common:button.plan_details_and_book')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelGeneralInfo;
